//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import moment from 'moment-timezone';
import React from 'react';
import styled, { css } from 'styled-components';

import { PurchaseOption } from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { DATE_FORMAT_MMM_DD_YYYY } from '@campaigns/constants/DateConstants';
import {
  WIDTH_EXPIRATION_DATE,
  WIDTH_UNITS,
} from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import {
  getExpiryBadgeText,
  getExpiryBadgeType,
} from '@campaigns/utils/sampling/samplingCampaignsPurchaseOptionsUtils';
import { Badge } from '@components/atoms/badge/Badge';
import {
  TableDataPrimaryText,
  TableDataSecondaryText,
} from '@components/atoms/text/Text';
import { TableDataCell } from '@components/organisms/table/TableDataCell';
import { isDefined } from '@utils/filterPredicates';

interface PurchaseOptionsTableRowContentProps {
  expiryBadgeInfo: ReturnType<typeof getExpiryBadgeType>;
  isExpired: boolean;
  purchaseOption: PurchaseOption;
}

/**
 * Shared row content for both readonly and editable modes.
 * Renders the purchase option data cells (ID, ref/attributes, units, expiry date).
 */
export const PurchaseOptionsTableRowContent = ({
  expiryBadgeInfo,
  isExpired,
  purchaseOption,
}: PurchaseOptionsTableRowContentProps) => {
  return (
    <>
      <TableDataCell>{purchaseOption.opportunityId}</TableDataCell>
      <TableDataCell>
        <StyledTableDataPrimaryText
          isExpired={isExpired}
          justifyContent='flex-start'
        >
          {purchaseOption.opportunityRef}
        </StyledTableDataPrimaryText>
        <StyledTableDataSecondaryText
          isExpired={isExpired}
          justifyContent='flex-start'
        >
          {purchaseOption.attributes}
        </StyledTableDataSecondaryText>
      </TableDataCell>
      <NumberCell>
        <StyledTableDataPrimaryText
          isExpired={isExpired}
          justifyContent='flex-end'
        >
          {purchaseOption.balance}
        </StyledTableDataPrimaryText>
      </NumberCell>
      <ExpirationDateCell>
        <StyledTableDataPrimaryText
          isExpired={isExpired}
          justifyContent='flex-start'
        >
          {moment(purchaseOption.expiryDate).format(DATE_FORMAT_MMM_DD_YYYY)}
        </StyledTableDataPrimaryText>
        {isDefined(expiryBadgeInfo) && (
          <Badge
            size='small'
            text={getExpiryBadgeText(expiryBadgeInfo.daysUntilExpiry)}
            type={expiryBadgeInfo.type}
          />
        )}
      </ExpirationDateCell>
    </>
  );
};

const expiredTextStyles = css`
  color: ${({ theme }) => theme.colors.gray7};
`;

const NumberCell = styled(TableDataCell)`
  ${({ theme }) => {
    return css`
      ${WIDTH_UNITS({ theme })}
      padding-right: ${theme.spacings[6]};
    `;
  }}
`;

const ExpirationDateCell = styled(TableDataCell)`
  ${WIDTH_EXPIRATION_DATE}
`;

export const StyledTableDataPrimaryText = styled(TableDataPrimaryText)<{
  isExpired: boolean;
}>`
  ${({ isExpired }) => isExpired && expiredTextStyles}
`;

export const StyledTableDataSecondaryText = styled(TableDataSecondaryText)<{
  isExpired: boolean;
}>`
  ${({ isExpired }) => isExpired && expiredTextStyles}
`;
